import React, { useState, useContext } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import { AuthContext } from '../../context/AuthContext';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';

/**
 * The cashier's release action.
 *
 * Deliberately NOT part of ApprovalActions: releasing funds is not a fourth approval
 * stage. It is also not a payment record — no method, no reference. It captures only that
 * the money was handed over, by whom and when. The spend itself is accounted for later,
 * when the accountant captures it as an expense.
 *
 * Has its own permission (`requisitions.issue`) so whoever approves need not be whoever
 * hands the money over, and only appears once the full approval chain is complete.
 */
const RequisitionIssueAction = ({ requisition, onChanged, size = 'small' }) => {
  const { formatMoney } = useCurrency();
  const { can } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');

  const alreadyIssued = !!requisition.issuedAt;
  const isApproved = requisition.approvalStatus === 'approved';

  // Mirrors resolveIssue() on the server: approved, and not already released.
  if (!can('requisitions', 'issue') || !isApproved || alreadyIssued) return null;

  const handleIssue = async () => {
    setBusy(true);
    try {
      await axios.post(`/requisitions/${requisition._id}/issue`, {
        comment: comment.trim() || undefined,
      });
      toast.success('Funds released and recorded');
      setOpen(false);
      setComment('');
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not record the release');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant="contained"
        color="primary"
        startIcon={<PaymentsIcon />}
        onClick={() => setOpen(true)}
      >
        Release Funds
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Release Funds</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This records that the money has been handed over. It cannot be undone, and funds
            can only be released once.
          </Alert>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is not an accounting entry. The spend must still be accounted for and
            captured as an expense once it has been made.
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {requisition.requisitionNumber} · {requisition.purpose}
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>
              {formatMoney(requisition.amount)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Requested by {requisition.createdBy?.name || 'Unknown'}
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={3}
            margin="normal"
            label="Notes"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            helperText="Optional — kept in the audit trail"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleIssue} variant="contained" disabled={busy}>
            {busy ? 'Recording...' : 'Confirm Release'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequisitionIssueAction;
